import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, limit, doc, deleteDoc, startAfter, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { isAdmin, isPartner } from '../config/accessControl';

const PAGE_SIZE = 50;

// Helper function for formatting date/time
const formatDateTime = (timestampInput) => {
  if (!timestampInput) return 'N/A';
  try {
    let date;
    if (typeof timestampInput === 'string') {
      date = new Date(timestampInput);
      if (isNaN(date.getTime())) return timestampInput;
    } else if (timestampInput && typeof timestampInput.toDate === 'function') { // Firestore Timestamp
      date = timestampInput.toDate();
    } else if (timestampInput instanceof Date) {
      date = timestampInput;
    } else {
      return String(timestampInput);
    }
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
    }).format(date);
  } catch (e) {
    return String(timestampInput);
  }
};

function Examinations({ selectedCompany, userEmail }) {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserPartner, setIsUserPartner] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const checkUserRoles = async () => {
      if (userEmail) {
        setIsUserAdmin(await isAdmin(userEmail));
        setIsUserPartner(await isPartner(userEmail));
      } else {
        setIsUserAdmin(false);
        setIsUserPartner(false);
      }
    };
    checkUserRoles();
  }, [userEmail]);

  useEffect(() => {
    if (selectedCompany) {
      fetchItems(true);
    } else {
      setItems([]);
      setSelectedItem(null);
      setTotalCount(0);
      setHasMore(true);
      setLastVisible(null);
    }
    setSelectedItem(null);
  }, [selectedCompany]);

  const fetchItems = async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const examinationsRef = collection(db, 'production', selectedCompany, 'examinations');
      let q;

      if (isInitial) {
        q = query(examinationsRef, orderBy('created_at', 'desc'), limit(PAGE_SIZE));
        const countSnapshot = await getCountFromServer(examinationsRef);
        setTotalCount(countSnapshot.data().count);
      } else {
        q = query(examinationsRef, orderBy('created_at', 'desc'), startAfter(lastVisible), limit(PAGE_SIZE));
      }

      const querySnapshot = await getDocs(q);
      const examinationsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      if (isInitial) {
        setItems(examinationsList);
        if (examinationsList.length > 0) setSelectedItem(examinationsList[0]);
        else setSelectedItem(null);
      } else {
        setItems(prev => [...prev, ...examinationsList]);
      }

      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMore(querySnapshot.docs.length === PAGE_SIZE);

    } catch (error) {
      console.error('Error fetching examinations:', error);
      setError(`Failed to load examinations: ${error.message}`);
    } finally {
      if (isInitial) setLoading(false);
      else setLoadingMore(false);
    }
  };

  const handleSelectItem = (item) => {
    setSelectedItem(item);
  };

  const handleDeleteItem = async (itemId, itemCompany) => {
    if (!window.confirm('Are you sure you want to delete this examination? This action cannot be undone.')) {
      return;
    }

    setDeletingId(itemId);
    setError(null);

    try {
      await deleteDoc(doc(db, 'production', itemCompany, 'examinations', itemId));

      setItems(prevItems => prevItems.filter(item => item.id !== itemId));
      if (selectedItem?.id === itemId) {
        const newItems = items.filter(item => item.id !== itemId);
        setSelectedItem(newItems.length > 0 ? newItems[0] : null);
      }

    } catch (err) {
      console.error('Error deleting examination:', err);
      setError(`Failed to delete examination: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  if (!selectedCompany) {
    return <div className="p-4 text-gray-500">Please select a company to view examinations.</div>;
  }

  return (
    <div className="flex flex-col md:flex-row gap-4">
      {/* Left panel - List of examinations */}
      <div className="w-full md:w-1/3 bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Examinations</h3>
          <p className="text-sm text-gray-500">
            {totalCount > 0 ? `${totalCount} total` : `${items.length} results`}
            {items.length > 0 && items.length < totalCount && ` (${items.length} loaded)`}
          </p>
        </div>

        {loading && <div className="p-4 text-center text-gray-500">Loading...</div>}
        {error && (
          <div className="p-4 m-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
            <button onClick={() => fetchItems(true)} className="ml-4 px-2 py-1 bg-red-200 rounded hover:bg-red-300 text-sm">
              Retry
            </button>
          </div>
        )}

        <div className="overflow-y-auto max-h-[60vh]">
          {items.map((item) => (
            <div
              key={item.id}
              className={`block p-4 border-b cursor-pointer hover:bg-gray-100 transition duration-150 ease-in-out ${selectedItem?.id === item.id ? 'bg-blue-50 border-l-4 border-blue-500' : 'border-l-4 border-transparent'}`}
              onClick={() => handleSelectItem(item)}
            >
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-gray-800 truncate" title={item.name}>{item.name || 'Unnamed Patient'}</span>
                <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                  {item.day || 'No Date'}, {item.time || 'No Time'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {item.examinations?.length || 0} examination(s)
                </span>
                <span className="text-sm font-semibold text-blue-600">
                  {item.total_price || 'N/A'}
                </span>
              </div>
            </div>
          ))}
          {items.length === 0 && !loading && !error && (
            <div className="p-4 text-center text-gray-500">
              No examinations found for {selectedCompany}.
            </div>
          )}

          {hasMore && items.length > 0 && (
            <div className="p-4 text-center border-t">
              <button
                onClick={() => fetchItems(false)}
                disabled={loadingMore}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? 'Loading...' : 'Show more'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right panel - Examination details */}
      <div className="w-full md:w-2/3 bg-white rounded-lg shadow">
        {selectedItem ? (
          <div className="flex flex-col h-full">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Examination Details</h3>
                  <p className="text-xl font-semibold text-gray-800 mt-1">{selectedItem.name || 'N/A'}</p>
                  <p className="text-sm text-gray-500">
                    {selectedItem.day || 'N/A'}, {selectedItem.time || 'N/A'}
                  </p>
                </div>
                {(isUserAdmin || isUserPartner) && (
                  <button
                    onClick={() => handleDeleteItem(selectedItem.id, selectedCompany)}
                    disabled={deletingId === selectedItem.id}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed self-start"
                  >
                    {deletingId === selectedItem.id ? 'Deleting...' : 'Delete Examination'}
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto flex-grow">
              {/* Total Price - Prominent Display */}
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-300">
                <h4 className="text-sm font-medium text-blue-600 uppercase tracking-wider mb-1">Total Price</h4>
                <p className="text-3xl font-bold text-blue-900">{selectedItem.total_price || 'N/A'}</p>
              </div>

              {/* Patient Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InfoItem label="Phone" value={selectedItem.phone} />
                <InfoItem label="Company" value={selectedItem.company} />
                <InfoItem label="Created At" value={formatDateTime(selectedItem.created_at)} />
              </div>

              {/* Examinations List */}
              {selectedItem.examinations && selectedItem.examinations.length > 0 && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Examinations List</h4>
                  <div className="space-y-2">
                    {selectedItem.examinations.map((exam, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                        <span className="text-sm font-medium text-gray-800">{exam.name || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{exam.price || 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedItem.address && <InfoItem label="Address" value={selectedItem.address} />}
              <InfoItem label="Conversation ID" value={selectedItem.conversation_id} breakWord={true} />
              <InfoItem label="Document ID" value={selectedItem.doc_id} breakWord={true} />
              <InfoItem label="Type" value={selectedItem.type} />
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 flex items-center justify-center h-full">
            {loading ? 'Loading...' : 'Select an examination to view details.'}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper component for consistent styling of info items
const InfoItem = ({ label, value, breakWord = false }) => (
  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</h4>
    <p className={`text-sm font-medium text-gray-800 ${breakWord ? 'break-all' : ''}`}>{value !== null && value !== undefined ? String(value) : 'N/A'}</p>
  </div>
);

export default Examinations;
